import { useMemo, type ComponentType, type ReactElement } from "react";
import { AbsoluteFill, Sequence, useCurrentFrame } from "remotion";
import { resolveTracks } from "./tracks";
import {
  assertSupportedCompositionVersion,
  isCustomComponentLayer,
  LAYER_COMPONENT_AUDIO,
  LAYER_COMPONENT_CAPTIONS,
  LAYER_COMPONENT_HTML_OVERLAY,
  LAYER_COMPONENT_VIDEO,
  type CompositionLayer,
  type LayerTransform,
  type VideoComposition,
} from "./types";
import { useAssetResolver } from "./asset-resolver-context";
import { getLayerTrim } from "./layer-trim";
import { AudioLayer } from "./components/audio";
import { CaptionLayer } from "./components/captions";
import { HtmlOverlayLayer } from "./components/html-overlay";
import { VideoLayer } from "./components/video";
import { resolveCropWindow } from "./components/video-crop-style";
import { usePlayerBuffering } from "./components/use-player-buffering";
import { holdsLastFrame } from "./hold-last-frame";

export const COMPONENT_REGISTRY: Record<string, ComponentType<any>> = {
  [LAYER_COMPONENT_AUDIO]: AudioLayer,
  [LAYER_COMPONENT_CAPTIONS]: CaptionLayer,
  [LAYER_COMPONENT_HTML_OVERLAY]: HtmlOverlayLayer,
  [LAYER_COMPONENT_VIDEO]: VideoLayer,
};

/**
 * Return a copy of `composition` with custom-component layers filtered
 * out. Use in contexts where the renderer can't execute agent-generated
 * JS — primarily thumbnails, which render inline (no sandbox iframe) and
 * therefore must skip custom components for security.
 */
export function withoutCustomComponentLayers(
  composition: VideoComposition,
): VideoComposition {
  const filtered = composition.layers.filter(
    (layer) => !isCustomComponentLayer(layer),
  );
  if (filtered.length === composition.layers.length) return composition;
  return { ...composition, layers: filtered };
}

function resolveValue(
  value: unknown,
  resolve: (ref: string) => string,
): unknown {
  if (typeof value === "string" && value.startsWith("ad:")) {
    return resolve(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => resolveValue(item, resolve));
  }
  if (value !== null && typeof value === "object") {
    return resolveAdRefsInProps(value as Record<string, unknown>, resolve);
  }
  return value;
}

export function resolveAdRefsInProps(
  props: Record<string, unknown>,
  resolve: (ref: string) => string,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    result[key] = resolveValue(value, resolve);
  }
  return result;
}

function resolveComponent(
  name: string,
  customRegistry?: Record<string, ComponentType<any>> | null,
): ComponentType<any> | undefined {
  // Custom registry takes precedence, allowing overrides of built-in components
  if (customRegistry?.[name]) return customRegistry[name];

  if (COMPONENT_REGISTRY[name]) return COMPONENT_REGISTRY[name];

  // Custom components: "./assets/foo.tsx" or "./assets/foo.js"
  if (name.startsWith("./assets/") && customRegistry) {
    const filename = name.slice("./assets/".length).replace(/\.tsx$/, ".js");
    return customRegistry[filename];
  }

  return undefined;
}

const VISIBLE_WHILE_POSTMOUNTED = { opacity: 1 };

export function CompositionRoot({
  composition,
  customRegistry,
}: {
  composition: VideoComposition;
  customRegistry?: Record<string, ComponentType<any>> | null;
}) {
  const resolve = useAssetResolver();
  const frame = useCurrentFrame();
  const buffering = usePlayerBuffering();

  // A format this build predates throws — preview and Lambda alike — rather
  // than rendering without its animations.
  assertSupportedCompositionVersion(composition);

  // Layers paint in track order rather than array order, and a composition with
  // no stored tracks has one derived for it — see `resolveTracks`. Memoized
  // because the Player re-renders this on every frame while the layout only
  // changes when the composition does.
  const { paintOrder, hiddenLayerIds, totalFrames } = useMemo(() => {
    const { paintOrder, tracks, trackIdByLayerId } = resolveTracks(composition);
    const hiddenTrackIds = new Set(
      tracks
        .filter((resolved) => resolved.track.hidden)
        .map((resolved) => resolved.track.id),
    );
    const hiddenLayerIds = new Set(
      paintOrder
        .filter((layer) => {
          const trackId = trackIdByLayerId.get(layer.id);
          return trackId != null && hiddenTrackIds.has(trackId);
        })
        .map((layer) => layer.id),
    );
    const totalFrames = Math.max(
      1,
      ...composition.layers.map((l) => l.from + l.duration),
    );
    return { paintOrder, hiddenLayerIds, totalFrames };
  }, [composition]);

  // Lead time for a video layer to open its file and decode its first frames
  // before it becomes visible. A clip on a cold CDN edge needs two range
  // requests before its first frame, and each cache miss measured 1.2-2.6s.
  // Bounded rather than the whole composition: every premounted clip holds an
  // open WebCodecs decoder, and premounting them all from frame 0 hangs the
  // Player's buffer state (see the Sequence below).
  const videoPremountFrames = Math.round(composition.fps * 5);

  // Built once per composition, not per frame: resolving refs copies every
  // prop object, and fresh props on every frame defeat each layer's own
  // memoization (the HTML overlay re-parsed and re-rendered its whole doc).
  // A stable element also lets React skip the subtree when this root
  // re-renders for the frame-dependent Sequence style below.
  const layerElements = useMemo(() => {
    const elements: { layer: CompositionLayer; element: ReactElement }[] = [];
    for (const layer of paintOrder) {
      // Editor-managed hidden state (persisted on the layer's props, or on the
      // track the layer sits on) removes the layer from every render context —
      // preview, thumbnail, and the exported creative — mirroring how the
      // static editor's `display:none` carries into its saved HTML.
      if ((layer.props as { hidden?: boolean }).hidden) continue;
      if (hiddenLayerIds.has(layer.id)) continue;

      const Component = resolveComponent(layer.component, customRegistry);
      if (!Component) {
        console.warn(`Unknown composition component: ${layer.component}`);
        continue;
      }

      const props = resolveAdRefsInProps(layer.props, resolve);
      // Strip `transform` before forwarding to the component — it's handled
      // by the wrapper below, not by the component itself. Components ignore
      // unknown props anyway, but stripping keeps the rendered DOM clean.
      const { transform, ...rest } = props as {
        transform?: unknown;
      } & Record<string, unknown>;
      const trimStart = getLayerTrim(layer)?.startFrame ?? 0;
      // A video layer that crops without placing itself still needs a window
      // for the wrapper to clip against, and gets the canvas as one. The
      // component itself never sees it: `content` is relative to the window,
      // so the inner video positions itself. Video's legacy `props.trim` is
      // stripped the same way as `transform`: trim is applied by the inner
      // Sequence below, and only video ever wrote that prop, so other
      // components keep a `trim` prop of their own.
      // The media layers fade against their clip length, which they cannot
      // read for themselves: inside the trim Sequence below,
      // `useVideoConfig()` reports `layer.duration + trimStart`. Remotion's
      // volume callback already reports visible-layer time, so the trim
      // in-point is not injected.
      let componentProps: Record<string, unknown> = rest;
      let layerWindow: unknown = transform;
      if (layer.component === LAYER_COMPONENT_VIDEO) {
        const { trim: _legacyTrim, ...videoProps } = rest;
        layerWindow = resolveCropWindow(
          transform,
          videoProps.content,
          composition,
        );
        componentProps = { ...videoProps, durationInFrames: layer.duration };
      } else if (layer.component === LAYER_COMPONENT_AUDIO) {
        componentProps = { ...rest, durationInFrames: layer.duration };
      }

      elements.push({
        layer,
        element: (
          // Trim, applied once for every component: a negative `from` is
          // exactly what Remotion's own `trimBefore` does, so any media below
          // sees the offset through `useMediaStartsAt` and every other
          // component through `useCurrentFrame`. That hook reads only the
          // nearest Sequence, so nothing between here and the component may be
          // another <Sequence>.
          <Sequence from={-trimStart} layout="none">
            <LayerTransformWrapper transform={layerWindow}>
              <Component {...componentProps} />
            </LayerTransformWrapper>
          </Sequence>
        ),
      });
    }
    return elements;
  }, [paintOrder, hiddenLayerIds, customRegistry, resolve, composition]);

  return (
    <AbsoluteFill>
      {layerElements.map(({ layer, element }) => {
        return (
          <Sequence
            key={layer.id}
            from={layer.from}
            durationInFrames={layer.duration}
            // Layers stay mounted for the whole composition. Premounting avoids
            // flickering (https://www.remotion.dev/docs/troubleshooting/player-flicker);
            // postmounting keeps a layer alive after its clip ends so a looping
            // Player replays it instead of remounting and refetching every loop.
            // Video is the exception: premounted for the whole composition it
            // hangs the Player in its buffer state when an unmuted clip reaches
            // its entry frame, and only a seek clears it, so it gets a bounded
            // window instead.
            // Both props are Player-only — Remotion ignores them when rendering.
            premountFor={
              layer.component === LAYER_COMPONENT_VIDEO
                ? videoPremountFrames
                : totalFrames
            }
            postmountFor={totalFrames}
            styleWhilePostmounted={
              layer.component === LAYER_COMPONENT_VIDEO &&
              holdsLastFrame({
                frame,
                layerEnd: layer.from + layer.duration,
                fps: composition.fps,
                buffering,
              })
                ? VISIBLE_WHILE_POSTMOUNTED
                : undefined
            }
          >
            {element}
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
}

/**
 * Wrap a layer's output in an absolutely-positioned container when a
 * spatial `transform` is set. Without a transform we render the children
 * directly so the layer's own `<AbsoluteFill>` continues to fill the full
 * composition (the historical behavior).
 *
 * Invalid transforms (missing fields, non-finite numbers, zero/negative
 * size) are treated as "no transform" — safer than rendering an invisible
 * or NaN-positioned layer when bad data sneaks in from the DB.
 */
function LayerTransformWrapper({
  transform,
  children,
}: {
  transform: unknown;
  children: React.ReactNode;
}) {
  const valid = isValidTransform(transform);
  if (!valid) return <>{children}</>;
  return (
    <div
      style={{
        position: "absolute",
        left: `${valid.x}px`,
        top: `${valid.y}px`,
        width: `${valid.width}px`,
        height: `${valid.height}px`,
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

function isValidTransform(value: unknown): LayerTransform | null {
  if (!value || typeof value !== "object") return null;
  const t = value as Record<string, unknown>;
  const { x, y, width, height } = t;
  if (
    typeof x !== "number" ||
    typeof y !== "number" ||
    typeof width !== "number" ||
    typeof height !== "number" ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return { x, y, width, height };
}
